/**
 * Shared utilities for the GoalOverlayLayer canvas component.
 *
 * Provides:
 *  - LANDMARK_NAME_TO_INDEX        — snake_case landmark name → MediaPipe index
 *  - VIRTUAL_LANDMARK_PAIRS        — virtual midpoint landmarks (mid_ankle, mid_ear, etc.)
 *  - GoalType / classifyGoalKey    — derive goal type from result map key suffix
 *  - scoreToColor                  — score (0-100) → dark-theme hex color
 *  - getLandmarkPosition           — look up a landmark's normalized position for a frame
 *  - drawScoreBadge                — draw a small badge on a canvas context
 */

import { PoseLandmark } from '@/types/mediapipe';
import { GOAL_OVERLAY } from '@/utils/visualizationConstants';
import { MIRROR_INDEX_MAP } from '@/utils/poseUtils';
import type { RawPoseFrame } from '@/types/api';

// ---------------------------------------------------------------------------
// Landmark name map (snake_case → MediaPipe index)
// ---------------------------------------------------------------------------

/**
 * Maps the snake_case landmark names used in goal configs (e.g. "left_shoulder")
 * to their corresponding MediaPipe PoseLandmark index (0-32).
 */
export const LANDMARK_NAME_TO_INDEX: Record<string, number> = {
  nose:              PoseLandmark.NOSE,
  left_eye_inner:    PoseLandmark.LEFT_EYE_INNER,
  left_eye:          PoseLandmark.LEFT_EYE,
  left_eye_outer:    PoseLandmark.LEFT_EYE_OUTER,
  right_eye_inner:   PoseLandmark.RIGHT_EYE_INNER,
  right_eye:         PoseLandmark.RIGHT_EYE,
  right_eye_outer:   PoseLandmark.RIGHT_EYE_OUTER,
  left_ear:          PoseLandmark.LEFT_EAR,
  right_ear:         PoseLandmark.RIGHT_EAR,
  mouth_left:        PoseLandmark.MOUTH_LEFT,
  mouth_right:       PoseLandmark.MOUTH_RIGHT,
  left_shoulder:     PoseLandmark.LEFT_SHOULDER,
  right_shoulder:    PoseLandmark.RIGHT_SHOULDER,
  left_elbow:        PoseLandmark.LEFT_ELBOW,
  right_elbow:       PoseLandmark.RIGHT_ELBOW,
  left_wrist:        PoseLandmark.LEFT_WRIST,
  right_wrist:       PoseLandmark.RIGHT_WRIST,
  left_pinky:        PoseLandmark.LEFT_PINKY,
  right_pinky:       PoseLandmark.RIGHT_PINKY,
  left_index:        PoseLandmark.LEFT_INDEX,
  right_index:       PoseLandmark.RIGHT_INDEX,
  left_thumb:        PoseLandmark.LEFT_THUMB,
  right_thumb:       PoseLandmark.RIGHT_THUMB,
  left_hip:          PoseLandmark.LEFT_HIP,
  right_hip:         PoseLandmark.RIGHT_HIP,
  left_knee:         PoseLandmark.LEFT_KNEE,
  right_knee:        PoseLandmark.RIGHT_KNEE,
  left_ankle:        PoseLandmark.LEFT_ANKLE,
  right_ankle:       PoseLandmark.RIGHT_ANKLE,
  left_heel:         PoseLandmark.LEFT_HEEL,
  right_heel:        PoseLandmark.RIGHT_HEEL,
  left_foot_index:   PoseLandmark.LEFT_FOOT_INDEX,
  right_foot_index:  PoseLandmark.RIGHT_FOOT_INDEX,
};

// ---------------------------------------------------------------------------
// Virtual midpoint landmarks
// ---------------------------------------------------------------------------

/**
 * Some goal annotations use virtual landmarks (e.g. "mid_ankle") that are not
 * a single MediaPipe index but the midpoint of two named landmarks.  These
 * cannot be looked up via LANDMARK_NAME_TO_INDEX.
 *
 * Each entry maps the virtual name to a pair of constituent landmark names.
 * getLandmarkPosition() checks this map when the primary lookup returns
 * undefined and computes the average position.
 *
 * Matches backend functional_landmark_definitions.py virtual landmark names.
 */
export const VIRTUAL_LANDMARK_PAIRS: Record<string, [string, string]> = {
  mid_ankle:    ['left_ankle',    'right_ankle'],
  mid_ear:      ['left_ear',      'right_ear'],
  mid_hip:      ['left_hip',      'right_hip'],
  mid_shoulder: ['left_shoulder', 'right_shoulder'],
  mid_knee:     ['left_knee',     'right_knee'],
  mid_wrist:    ['left_wrist',    'right_wrist'],
};

// ---------------------------------------------------------------------------
// Goal type classification
// ---------------------------------------------------------------------------

export type GoalType = 'spatial' | 'movement' | 'axis' | 'timing' | 'joint_angle';

/**
 * Derive the goal type from the key suffix used in `spatial_goal_results`.
 *
 * Key suffix conventions (from api.ts comment):
 *  - (no suffix)   → spatial
 *  - _movement     → movement
 *  - _axis         → axis
 *  - _timing       → timing
 *  - _joint_angle  → joint_angle
 */
export function classifyGoalKey(key: string): GoalType {
  if (key.endsWith('_joint_angle')) return 'joint_angle';
  if (key.endsWith('_axis')) return 'axis';
  if (key.endsWith('_timing')) return 'timing';
  if (key.endsWith('_movement')) return 'movement';
  return 'spatial';
}

// ---------------------------------------------------------------------------
// Score → color mapping (dark theme: green / amber / red)
// ---------------------------------------------------------------------------

/** Score (0-100) → dark-theme hex color string. */
export function scoreToColor(score: number): string {
  if (score >= 80) return '#19C37D'; // performance green
  if (score >= 60) return '#F5A524'; // amber
  return '#F05D5E';                  // red
}

// ---------------------------------------------------------------------------
// Landmark position lookup
// ---------------------------------------------------------------------------

/**
 * Look up a landmark's normalized {x, y} position at a given frame.
 *
 * Supports both real MediaPipe landmarks (LANDMARK_NAME_TO_INDEX) and virtual
 * midpoint landmarks (VIRTUAL_LANDMARK_PAIRS such as "mid_ankle").  Virtual
 * landmarks are computed as the average position of two constituent landmarks.
 * If either constituent is invisible / missing the whole virtual position is
 * treated as unavailable (returns null) rather than pulling to one side.
 *
 * @param poseData    - Array of pose frames (each has a `landmarks` array)
 * @param frameIndex  - Index into poseData
 * @param landmarkName - snake_case landmark name (e.g. "left_shoulder", "mid_ankle")
 * @param mirrored    - When true, flip x and swap to the opposite-side index
 * @returns Normalized {x, y} coordinates (0-1) or null if unavailable
 */
export function getLandmarkPosition(
  poseData: RawPoseFrame[] | undefined,
  frameIndex: number,
  landmarkName: string,
  mirrored: boolean,
): { x: number; y: number } | null {
  if (!poseData || frameIndex < 0 || frameIndex >= poseData.length) {
    return null;
  }

  const frame = poseData[frameIndex];
  if (!frame?.landmarks || !Array.isArray(frame.landmarks)) {
    return null;
  }

  // --- Virtual midpoint landmark path ---
  const virtualPair = VIRTUAL_LANDMARK_PAIRS[landmarkName];
  if (virtualPair) {
    const [nameA, nameB] = virtualPair;
    const posA = getLandmarkPosition(poseData, frameIndex, nameA, mirrored);
    const posB = getLandmarkPosition(poseData, frameIndex, nameB, mirrored);
    if (!posA || !posB) return null;
    return { x: (posA.x + posB.x) / 2, y: (posA.y + posB.y) / 2 };
  }

  // --- Standard landmark path ---
  let index = LANDMARK_NAME_TO_INDEX[landmarkName];
  if (index === undefined) {
    return null;
  }

  // When mirrored, swap to the opposite-side landmark index
  if (mirrored) {
    index = MIRROR_INDEX_MAP[index] ?? index;
  }

  const lm = frame.landmarks[index];
  if (!lm || lm.x === undefined || lm.y === undefined) {
    return null;
  }

  // When mirrored, flip the x-coordinate
  const x = mirrored ? 1.0 - lm.x : lm.x;
  return { x, y: lm.y };
}

// ---------------------------------------------------------------------------
// Anchor position lookup (barycentric axis mode)
// ---------------------------------------------------------------------------

/**
 * Retrieve the pre-computed anchor position for a given video frame index.
 *
 * The backend (or frontend reconstruction) produces parallel arrays:
 *   perFrameAnchorList[i] corresponds to validFrameIndices[i].
 *
 * Returns null when:
 *   - perFrameAnchorList is undefined
 *   - frameIndex is not in validFrameIndices
 *   - The entry at that position is null (landmark not visible that frame)
 */
export function getAnchorPosition(
  perFrameAnchorList: Array<[number, number] | null> | undefined,
  validFrameIndices: number[] | undefined,
  frameIndex: number,
): { x: number; y: number } | null {
  if (!perFrameAnchorList || !validFrameIndices) return null;

  const listPos = validFrameIndices.indexOf(frameIndex);
  if (listPos === -1) return null;

  const entry = perFrameAnchorList[listPos];
  if (!entry) return null;

  return { x: entry[0], y: entry[1] };
}

// ---------------------------------------------------------------------------
// Badge drawing
// ---------------------------------------------------------------------------

/**
 * Draw a compact score badge on a canvas at pixel coordinates (x, y).
 *
 * The badge is drawn with a semi-transparent dark background and text colored
 * by the score. An optional label is prepended to the score text (truncated).
 *
 * @param ctx   - Canvas 2D rendering context
 * @param x     - Pixel X position (badge left edge will be placed near here)
 * @param y     - Pixel Y position (badge centered vertically around here)
 * @param score - Score value 0-100
 * @param label - Optional short label (will be truncated in display)
 */
export function drawScoreBadge(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  score: number,
  label?: string,
): void {
  const color = scoreToColor(score);
  const text = label
    ? `${label.slice(0, 8)} ${Math.round(score)}`
    : `${Math.round(score)}`;

  ctx.font = GOAL_OVERLAY.BADGE_FONT;
  const textWidth = ctx.measureText(text).width;
  const padX = GOAL_OVERLAY.BADGE_PADDING.x;
  const badgeW = textWidth + padX * 2;
  const badgeH = 18; // fixed badge height keeps badges uniform

  const bx = x - badgeW / 2;
  const by = y - badgeH / 2;

  // Background
  ctx.fillStyle = GOAL_OVERLAY.BADGE_BG;
  ctx.beginPath();
  roundRect(ctx, bx, by, badgeW, badgeH, 4);
  ctx.fill();

  // Score text
  ctx.fillStyle = color;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, x, y + 1);

  // Reset alignment to defaults to avoid side-effects on subsequent drawing
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
}

// ---------------------------------------------------------------------------
// Internal canvas helpers
// ---------------------------------------------------------------------------

/**
 * Draw a rounded rectangle path on a canvas context.
 * Does not call fill() or stroke() — caller decides what to do with the path.
 */
function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  if (typeof ctx.roundRect === 'function') {
    ctx.roundRect(x, y, w, h, r);
    return;
  }
  // Fallback for environments that don't support ctx.roundRect
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.quadraticCurveTo(x + w, y, x + w, y + r);
  ctx.lineTo(x + w, y + h - r);
  ctx.quadraticCurveTo(x + w, y + h, x + w - r, y + h);
  ctx.lineTo(x + r, y + h);
  ctx.quadraticCurveTo(x, y + h, x, y + h - r);
  ctx.lineTo(x, y + r);
  ctx.quadraticCurveTo(x, y, x + r, y);
  ctx.closePath();
}
